import { NextPage } from 'next';
import React from 'react';
import { Header } from 'components/structures';
import {
  colocationDataCenters,
  edgeDataCenters,
  enterpriseDataCenters,
  greenDataCenters,
  heroSection,
  hyperscaleDataCenters,
  managedServicesDataCenters,
  modularDataCenters,
} from '../images/expertise/data-centers-capabilities';
import { DataCentersCapabilitiesSection } from '../containers/expertise';
import { Box } from '@material-ui/core';

const DataCentersCapabilities: NextPage = () => {
  return (
    <>
      <Header
        title="Data Centers Capabilities"
        description={
          <>
            Utilities One provides end-to-end data center solutions, from design and construction to management. Our Enterprise to
            Modular Data Centers are built to the highest standards in security, reliability, and efficiency, delivering scalable
            infrastructure tailored to diverse industry needs.
          </>
        }
        image={heroSection}
      />
      <Box mb={20} />
      <DataCentersCapabilitiesSection
        image={enterpriseDataCenters}
        title={'Enterprise Data Centers'}
        description={
          'Our Enterprise Data Centers provide full-service solutions to create resilient, high-performance facilities that meet regulatory standards, ensure data integrity, and support critical business operations.'
        }
        subtitle={'Capabilities:'}
        list={[
          {
            text: 'Design & Construction: Building robust structures, primary and backup power systems, and advanced HVAC for climate control.',
          },
          {
            text: 'Networking Infrastructure: Installation of routers, switches, and structured cabling for high-speed connectivity.',
          },
          { text: 'Power Solutions: Implementing UPS, PDUs, and backup generators to maintain continuous operation.' },
          { text: 'Security Systems: Biometric access control, surveillance systems, and fire suppression to protect assets.' },
          { text: 'Compliance: Adherence to local regulations and ISO, Tier, and industry standards.' },
        ]}
      />
      <Box mb={10} />
      <DataCentersCapabilitiesSection
        image={colocationDataCenters}
        title={'Colocation Data Centers'}
        description={
          'Designed for multi-client hosting, our Colocation Data Centers offer secure, high-availability spaces with optimized power, robust connectivity, and advanced cybersecurity.'
        }
        subtitle={'Capabilities:'}
        list={[
          { text: 'Rack Space Setup: High-density server racks with structured fiber optic and Ethernet cabling.' },
          { text: 'Redundant Systems: Integrated UPS and backup power for uninterrupted service.' },
          {
            text: 'Bandwidth Management: Collaboration with ISPs and configuration of network equipment for reliable data traffic.',
          },
          {
            text: 'Security Enhancements: Physical and cybersecurity measures, including fire suppression and intrusion detection.',
          },
        ]}
      />
      <Box mb={10} />
      <DataCentersCapabilitiesSection
        image={managedServicesDataCenters}
        title={'Managed Services Data Centers'}
        description={
          'Our Managed Services Data Centers provide dedicated infrastructure with secure servers, scalable storage, and robust backup and recovery solutions, meeting rigorous security compliance standards.'
        }
        subtitle={'Capabilities:'}
        list={[
          { text: 'Hardware and Software Management: Tailored deployment of high-performance servers, storage, and firewalls.' },
          {
            text: 'Backup and Recovery Systems: Data backup, recovery protocols, and routine testing for disaster preparedness.',
          },
          { text: 'User Management: Biometric and role-based access controls with user monitoring.' },
          { text: 'Security Compliance: Regular audits and adherence to ISO 27001, GDPR, HIPAA, and SSAE 18.' },
        ]}
      />
      <Box mb={10} />
      <DataCentersCapabilitiesSection
        image={hyperscaleDataCenters}
        title={'Hyperscale Data Centers'}
        description={
          'Our Hyperscale Data Centers are optimized for high efficiency, integrating renewable energy sources, automation, and advanced cooling to support large-scale operations with minimal environmental impact.'
        }
        subtitle={'Capabilities:'}
        list={[
          {
            text: 'Energy Efficiency: Renewable energy integration, power usage optimization, and intelligent power management.',
          },
          { text: 'Automation Systems: Automated provisioning, real-time monitoring, and self-healing capabilities.' },
          { text: 'Massive Scale Networking: High-performance networking equipment and load balancing for traffic management.' },
          { text: 'Advanced Cooling Systems: Liquid cooling, hot/cold aisle containment, and energy-efficient CRAC units.' },
        ]}
      />
      <Box mb={10} />
      <DataCentersCapabilitiesSection
        image={edgeDataCenters}
        title={'Edge Data Centers'}
        description={
          'Our Edge Data Centers are compact, mobile units designed for low-latency performance, ideal for remote locations and localized operations with seamless integration into existing networks.'
        }
        subtitle={'Capabilities:'}
        list={[
          { text: 'Compact Design & Mobility: Scalable modular units for efficient use of space and transportability.' },
          { text: 'Local Network Integration: Optimized network topology and hardware for reliable local connectivity.' },
          {
            text: 'Remote Management: Performance monitoring, management software, and automated alerts for proactive maintenance.',
          },
        ]}
      />
      <Box mb={10} />
      <DataCentersCapabilitiesSection
        image={greenDataCenters}
        title={'Green Data Centers'}
        description={
          'Built with a focus on sustainability, our Green Data Centers utilize renewable energy sources, eco-friendly materials, and energy-efficient equipment to minimize environmental impact.'
        }
        subtitle={'Capabilities:'}
        list={[
          { text: 'Renewable Energy Solutions: Solar panels, wind turbines, and battery storage for a consistent power supply.' },
          { text: 'Energy-Efficient Equipment: Certified low-power servers and advanced cooling systems.' },
          { text: 'Eco-Friendly Design: Sustainable materials, green building standards, and water conservation systems.' },
        ]}
      />
      <Box mb={10} />
      <DataCentersCapabilitiesSection
        image={modularDataCenters}
        title={'Modular Data Centers'}
        description={
          'Our Modular Data Centers are pre-fabricated units engineered for rapid deployment and adaptability to diverse environments, offering flexible and reliable infrastructure solutions.'
        }
        subtitle={'Capabilities:'}
        list={[
          { text: 'Modular Assembly: Quick installation with pre-configured components for easy expansion.' },
          { text: 'Rapid Deployment Systems: Site preparation and plug-and-play setup for immediate operational readiness.' },
          { text: 'Environmental Adaptability: Climate control and durable materials to withstand extreme weather conditions.' },
        ]}
      />
    </>
  );
};

export default DataCentersCapabilities;
